import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {NotificationSliceProps, FetchNotificationsRespons, Notification} from './types'
import {fetchNotifications, fetchNotificationsCount, updateNotification, markAllNotificationsRead, deleteNotification} from './service'

const initSlice: NotificationSliceProps = {
  listLoading: false,
  error: undefined,
  notifications: [],
  unreadCount: undefined,
}

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState: initSlice,
  reducers: {
    resetNotifications: (state: NotificationSliceProps) => {
      state.notifications = [];
    },
    addRealTimeNotification: (state: NotificationSliceProps, action: PayloadAction<Notification>) => {
      // Check if notification already exists to avoid duplicates
      const exists = state.notifications.some((n) => n._id === action.payload._id)
      if (!exists) {
        // Add new notification at the beginning of the array
        state.notifications = [action.payload, ...state.notifications]
      }
    },
    updateNotificationReadStatus: (state: NotificationSliceProps, action: PayloadAction<{ notificationId: string; read: boolean }>) => {
      const notification = state.notifications.find((n) => n._id === action.payload.notificationId)
      if (notification) {
        notification.read = action.payload.read
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchNotifications.pending, (state: NotificationSliceProps) => {
      state.listLoading = true
      state.error = undefined
    })
    builder.addCase(
      fetchNotifications.fulfilled,
      (state: NotificationSliceProps, action: PayloadAction<FetchNotificationsRespons>) => {
        // Merge API notifications with existing Socket.IO notifications
        // Socket.IO notifications might not be in API response yet (race condition)
        const apiNotifications = action.payload.notifications || []
        const existingNotifications = state.notifications || []
        
        // Create a map of existing notifications by ID
        const existingMap = new Map(existingNotifications.map(n => [n._id, n]))
        
        // Add/update notifications from API
        apiNotifications.forEach(apiNotif => {
          existingMap.set(apiNotif._id, apiNotif)
        })
        
        // Convert back to array and sort by createdAt (newest first)
        const merged = Array.from(existingMap.values()).sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime()
          const dateB = new Date(b.createdAt).getTime()
          return dateB - dateA
        })
        
        state.notifications = merged
        state.unreadCount = merged.filter((n) => !n.read).length
        state.listLoading = false
        state.error = undefined
        
        if (process.env.NODE_ENV === 'development') {
          console.debug('[notifications] Fetched from API:', apiNotifications.length, 'Total after merge:', merged.length)
        }
      }
    )
    builder.addCase(fetchNotifications.rejected, (state: NotificationSliceProps) => {
      state.listLoading = false
      state.error = 'error'
    })
    builder.addCase(fetchNotificationsCount.fulfilled, (state: NotificationSliceProps, action) => {
      state.unreadCount = action.payload.unreadCount
    })
    builder.addCase(updateNotification.fulfilled, (state: NotificationSliceProps, action) => {
      const id = action.meta.arg.notificationId
      const notification = state.notifications.find((n) => n._id === id)
      if (notification && !notification.read) {
        notification.read = true
        if (state.unreadCount !== undefined) state.unreadCount = Math.max(0, state.unreadCount - 1)
      }
    })
    builder.addCase(markAllNotificationsRead.fulfilled, (state: NotificationSliceProps) => {
      state.notifications = state.notifications.map((n) => ({ ...n, read: true }))
      state.unreadCount = 0
    })
    builder.addCase(deleteNotification.fulfilled, (state: NotificationSliceProps, action) => {
      const removed = state.notifications.find((n) => n._id === action.payload.id)
      state.notifications = state.notifications.filter((n) => n._id !== action.payload.id)
      if (state.unreadCount !== undefined && removed && !removed.read) state.unreadCount = Math.max(0, (state.unreadCount ?? 0) - 1)
    })
  },
})

const notificationsReducer = notificationsSlice.reducer
export const {actions} = notificationsSlice
export default notificationsReducer
